import {
  DataBlockVariant,
  QueryFactory,
  useEnabledDataBlockTypes,
} from '@lmt/shared-types';
import { ResourceDto, ResourceType } from '@lmt/shared-types';
import { Loading } from '@lmt/unicorn-shared/app/components/common/Loading';
import {
  UIConfigBlock,
  UIConfigurationType,
  UIConfigurator,
} from '@lmt/unicorn-shared/app/components/configurator/UIConfigurator';
import { useSubtenantsDict } from '@lmt/unicorn-shared/app/features/subtenants/selectors';
import { useUniSelector } from '@lmt/unicorn-shared/app/store/types';
import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { IncompleteVisitInfo } from './components/IncompleteVisitItem';
import { IncompleteVisitsPanel } from './components/IncompleteVisitsPanel';
import { NightViewInfo } from './components/NightViewItem';
import { NightViewPanel } from './components/NightViewPanel';
import { PlannedVisitsPanel } from './components/PlannedVisitsPanel';
import { RecentVisitInfo } from './components/RecentVisitItem';
import { RecentVisitsPanel } from './components/RecentVisitsPanel';
import { useTabs } from '@lmt/unicorn-shared/app/features/navigation/operations';
import { pages } from '@lmt/unicorn-shared/app/constants/sharedRoutes';
import { useUniDispatch } from '@lmt/unicorn-shared/app/store/types';
import { PlannedVisitInfo } from './components/PlannedVisitItem';
import {
  isActualNightVisit,
  isCurrentVisit,
  isIncompleteVisit,
  isPlannedVisit,
  isRecentVisit,
} from './components/common';
import { addDays, startOfToday } from 'date-fns';
import { getUIConfigurations } from '@lmt/unicorn-shared/app/features/ui-configurations/selector';
import { ConfigurationList } from '../configurator/configuration-list/ConfigurationList';
import { UIConfigurationsActions } from '@lmt/unicorn-shared/app/features/ui-configurations/reducer';
import { useClaims } from '@lmt/unicorn-shared/app/features/auth/operations';
import { useQueryClient } from '@tanstack/react-query';
import { useDataBlockBatchQuery } from './useDataBlockBatchQuery';

import styles from './Dashboard.module.scss';
import { NotesPanel } from './components/NotesPanel';
import { CurrentVisitInfo } from './components/CurrentVisitItem';
import { CurrentVisitsPanel } from './components/CurrentVisitsPanel';
import { sortResourceValues } from '../../helpers/sort';
import { useVisitTypes } from '../../features/dictionaries/selectors';
import {
  isMiniscreen,
  isStartMiniscreenOfflineModeEnabled,
  isStartMiniscreenOnlineModeEnabled,
} from '../patients/common';
import { isElectron } from '../../helpers/common';
import { useEventCallback } from '@mui/material';
import { useDataBlockTypeTranslation } from '../measurements/common';
import {
  useResourceManualOrdering,
  useSortedResources,
} from '../../api/custom-hooks';

const Dashboard: React.FC = React.memo(function Dashboard() {
  const i18next = useTranslation();
  const tabs = useTabs();
  const dispatch = useUniDispatch();
  const claims = useClaims();
  const queryClient = useQueryClient();
  const visitTypes = useVisitTypes();
  const subtenantsDict = useSubtenantsDict();
  const [isEdit, setEdit] = useState<boolean>(false);
  const dataBlockTypes = useEnabledDataBlockTypes();
  const translateDataBlockType = useDataBlockTypeTranslation();

  const { data: calendarSettings } =
    QueryFactory.CalendarSettingsQuery.useGetQuery();

  // Refresh dashboard every 10 minutes
  const [time, setTime] = useState(new Date().getTime());

  useEffect(() => {
    const interval = setInterval(() => setTime(new Date().getTime()), 600000);
    return () => {
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    if (calendarSettings?.timeZoneIanaId) {
      setTime(new Date().getTime());
    }
  }, [calendarSettings?.timeZoneIanaId]);

  const defaultConfigId =
    useUniSelector(getUIConfigurations).dashboardUIConfigurationId;
  const configurationsQuery =
    QueryFactory.UIConfigurationQuery.useGetDashboardConfigurationsQuery();

  const [editableConfigId, setEditableConfigId] = useState<number>();

  const changeConfig = useEventCallback((configId: number) => {
    dispatch(
      UIConfigurationsActions.dashboardUIConfigurationIdChanged(configId),
    );
  });

  const setDefaultConfig = useEventCallback(() => {
    if (configurationsQuery.data) {
      dispatch(
        UIConfigurationsActions.dashboardUIConfigurationIdChanged(
          configurationsQuery.data.find((config) => config.isDefault)?.id ??
            null,
        ),
      );
    }
  });

  useEffect(() => {
    if (configurationsQuery.data) {
      if (defaultConfigId) {
        // we check if config was removed
        const config = configurationsQuery.data.find(
          (config) => config.id === defaultConfigId,
        );
        if (!config) {
          setDefaultConfig();
        }
      } else {
        setDefaultConfig();
      }
    }
  }, [defaultConfigId, configurationsQuery.data]);

  // update current config after editing
  useEffect(() => {
    if (
      !isEdit &&
      defaultConfigId === editableConfigId &&
      !!configurationsQuery.data?.find(
        (config) => config.id === defaultConfigId,
      )
    ) {
      invalidateQuery(defaultConfigId);
    }
  }, [isEdit]);

  const invalidateQuery = useEventCallback(async (configId: number) => {
    await queryClient.invalidateQueries({
      queryKey:
        QueryFactory.UIConfigurationQuery.getConfigurationQueryKey(configId),
    });
  });

  const places = useSortedResources(ResourceType.Places, true);
  const manualOrdering = useResourceManualOrdering(ResourceType.Places);

  const placesDict = useMemo(() => {
    if (places) {
      const dict: { [key: number]: ResourceDto } = {};
      places.forEach((place, index) => {
        dict[place.id] = { ...place, manualOrderPosition: index };
      });
      return dict;
    } else return undefined;
  }, [places]);

  const visitsQuery = QueryFactory.AppointmentEventsQuery.useSearchVisitsQuery({
    from: addDays(startOfToday(), -7),
    to: addDays(startOfToday(), 4),
  });

  // Bug #79916: URL might be too long, we need to use batch query here
  const measurementQuery = useDataBlockBatchQuery({
    variant: DataBlockVariant.DeviceMeasurement,
    visitIds: visitsQuery.data?.map((x) => x.id),
  });

  const visitTypeById = useCallback(
    (typeId: number) =>
      i18next.t(
        (visitTypes.find((visitType) => visitType.id === typeId)?.value ||
          '-') as any,
      ),
    [visitTypes],
  );

  const nightVisits = useMemo(() => {
    if (visitsQuery.data && placesDict) {
      return visitsQuery.data
        .filter((visit) =>
          isActualNightVisit(visit.start, visit.end, visit.statusId),
        )
        .map((visit) => {
          const room = (visit.resourceIds ?? [])
            .filter((resourseId) => placesDict[resourseId])
            .map((resourseId) => placesDict[resourseId])[0];

          const subtenant = subtenantsDict[visit.subtenantId];
          const nightVisit: NightViewInfo = {
            id: visit.id,
            startDate: visit.start,
            endDate: visit.end,
            patientId: visit.patientId!,
            patientName: visit.patientName,
            room: room,
            subtenantCode: subtenant?.code,
            caseId: visit.caseId,
            visitType: visitTypeById(visit.typeId!),
            measurements:
              measurementQuery.data?.data
                .filter((x) => x.visitId === visit.id)
                .map((dataBlock) => {
                  return {
                    id: dataBlock.id,
                    name: translateDataBlockType(
                      dataBlockTypes.find((x) => x.id === dataBlock.typeId)!,
                    ) as string,
                  };
                }) ?? [],
          };

          return nightVisit;
        })
        .filter((x) => x)
        .sort((a, b) =>
          manualOrdering
            ? sortResourceValues(
                a.room?.manualOrderPosition.toString(),
                b.room?.manualOrderPosition.toString(),
              )
            : sortResourceValues(a.room?.name, b.room?.name),
        );
    } else return [];
  }, [visitsQuery.data, placesDict, time, measurementQuery.data]);

  const currentVisits = useMemo(() => {
    if (visitsQuery.data && placesDict) {
      return visitsQuery.data
        .filter((visit) =>
          isCurrentVisit(visit.start, visit.end, visit.statusId),
        )
        .map((visit) => {
          const room = (visit.resourceIds ?? [])
            .filter((resourseId) => placesDict[resourseId])
            .map((resourseId) => placesDict[resourseId])[0];

          const subtenant = subtenantsDict[visit.subtenantId];
          const currentVisit: CurrentVisitInfo = {
            id: visit.id,
            startDate: visit.start,
            endDate: visit.end,
            patientId: visit.patientId!,
            patientName: visit.patientName,
            room: room,
            subtenantCode: subtenant?.code,
            caseId: visit.caseId,
            visitType: visitTypeById(visit.typeId!),
          };

          return currentVisit;
        })
        .filter((x) => x)
        .sort((a, b) =>
          manualOrdering
            ? sortResourceValues(
                a.room?.manualOrderPosition.toString(),
                b.room?.manualOrderPosition.toString(),
              )
            : sortResourceValues(a.room?.name, b.room?.name),
        );
    } else return [];
  }, [visitsQuery.data, placesDict, time]);

  const incompleteVisits = useMemo(() => {
    if (visitsQuery.data) {
      return visitsQuery.data
        .filter((visit) => isIncompleteVisit(visit.end, visit.statusId))
        .map((visit) => {
          const subtenant = subtenantsDict[visit.subtenantId];
          const visitType = i18next.t(
            (visitTypes.find((visitType) => visitType.id === visit.typeId)
              ?.value || '-') as any,
          );
          const incompleteVisit: IncompleteVisitInfo = {
            id: visit.id,
            caseId: visit.caseId,
            startDate: visit.start,
            patientId: visit.patientId!,
            patientName: visit.patientName,
            subtenantCode: subtenant?.code,
            visitType: visitType,
          };

          return incompleteVisit;
        })
        .filter((x) => x)
        .sort((a, b) => a.startDate!.getTime() - b.startDate!.getTime());
    } else return [];
  }, [visitsQuery.data, manualOrdering]);

  const recentVisits = useMemo(() => {
    if (visitsQuery.data) {
      return visitsQuery.data
        .filter((visit) => isRecentVisit(visit.end, visit.statusId))
        .map((visit) => {
          const subtenant = subtenantsDict[visit.subtenantId];
          const visitType = i18next.t(
            (visitTypes.find((visitType) => visitType.id === visit.typeId)
              ?.value || '-') as any,
          );
          const recentVisit: RecentVisitInfo = {
            id: visit.id,
            caseId: visit.caseId,
            startDate: visit.start,
            endDate: visit.end,
            patientId: visit.patientId!,
            patientName: visit.patientName,
            subtenantCode: subtenant?.code,
            statusId: visit.statusId,
            visitType: visitType,
          };

          return recentVisit;
        })
        .filter((x) => x)
        .sort((a, b) => a.startDate!.getTime() - b.startDate!.getTime());
    } else return [];
  }, [visitsQuery.data]);

  const plannedVisits = useMemo(() => {
    if (visitsQuery.data) {
      return visitsQuery.data
        .filter((visit) => isPlannedVisit(visit.start))
        .map((visit) => {
          const subtenant = subtenantsDict[visit.subtenantId];
          const plannedVisit: PlannedVisitInfo = {
            id: visit.id,
            startDate: visit.start,
            patientId: visit.patientId!,
            patientName: visit.patientName,
            subtenantCode: subtenant?.code,
            patientContacts: visit.contacts ?? [],
            priorityContact: visit.priorityContact,
            statusId: visit.statusId,
            visitType: visitTypeById(visit.typeId!),
            caseId: visit.caseId,
          };

          return plannedVisit;
        })
        .filter((x) => x)
        .sort((a, b) => a.startDate!.getTime() - b.startDate!.getTime());
    } else return [];
  }, [visitsQuery.data, time]);

  const openPatientTab = useCallback((patientId: number) => {
    tabs.openTab(pages.patientProfile.link({ patientId: patientId }));
  }, []);

  const openCaseVisitTab = useCallback(
    (patientId: number, caseId: number, visitId: number) => {
      const itemId = `visit_${visitId}`;
      tabs.openTab(
        pages.patientCases.link({
          patientId: patientId,
          caseId: caseId,
        }),
        `item=${itemId}`,
      );
    },
    [],
  );

  const openMeasurementTab = useCallback(
    (
      patientId: number,
      caseId: number,
      visitId: number,
      measurementId: number,
    ) => {
      const itemId = `visit_${visitId}_measurement_${measurementId}`;
      tabs.openTab(
        pages.patientCases.link({
          patientId: patientId,
          caseId: caseId,
        }),
        `item=${itemId}`,
      );
    },
    [],
  );

  const plannedVisitsBlock = (
    <PlannedVisitsPanel
      plannedVisits={plannedVisits}
      openPatientTab={openPatientTab}
      openCaseVisitTab={openCaseVisitTab}
    />
  );
  const incompleteVisitsBlock = (
    <IncompleteVisitsPanel
      incompleteVisits={incompleteVisits}
      openCaseVisitTab={openCaseVisitTab}
      openPatientTab={openPatientTab}
    />
  );
  const recentVisitsBlock = (
    <RecentVisitsPanel
      recentVisits={recentVisits}
      openCaseVisitTab={openCaseVisitTab}
      openPatientTab={openPatientTab}
    />
  );
  const nightViewBlock = (
    <NightViewPanel
      nightVisits={nightVisits}
      openPatientTab={openPatientTab}
      openCaseVisitTab={openCaseVisitTab}
      openMeasurementTab={openMeasurementTab}
      startMiniscreenOnline={
        isElectron() &&
        isMiniscreen() &&
        isStartMiniscreenOnlineModeEnabled() &&
        claims.permissions.importMeasurements
      }
      startMiniscreenOffline={
        isElectron() &&
        isMiniscreen() &&
        isStartMiniscreenOfflineModeEnabled() &&
        claims.permissions.importMeasurements
      }
    />
  );

  const currentVisitsBlock = (
    <CurrentVisitsPanel
      currentVisits={currentVisits}
      openPatientTab={openPatientTab}
      openCaseVisitTab={openCaseVisitTab}
    />
  );

  const notesBlock = <NotesPanel />;

  const blocks: { [key: string]: UIConfigBlock } = {
    plannedVisits: {
      id: 'plannedVisits',
      component: plannedVisitsBlock,
      minW: 5,
      maxW: 12,
      minH: 5,
      w: 7,
      h: 12,
    },
    nightView: {
      id: 'nightView',
      component: nightViewBlock,
      minW: 3,
      maxW: 6,
      minH: 3,
      w: 5,
      h: 5,
    },
    incompleteVisits: {
      id: 'incompleteVisits',
      component: incompleteVisitsBlock,
      minW: 3,
      maxW: 6,
      minH: 3,
      w: 5,
      h: 3,
    },
    recentVisits: {
      id: 'recentVisits',
      component: recentVisitsBlock,
      minW: 3,
      maxW: 6,
      minH: 3,
      w: 5,
      h: 4,
    },
    currentVisits: {
      id: 'currentVisits',
      component: currentVisitsBlock,
      minW: 5,
      maxW: 12,
      minH: 3,
      w: 5,
      h: 5,
    },
    notes: {
      id: 'notes',
      component: notesBlock,
      minW: 3,
      maxW: 6,
      minH: 3,
      w: 5,
      h: 5,
    },
  };

  return (
    <>
      {!isEdit && (
        <div className={styles.header}>
          <div className={styles.title}>{i18next.t('tabs.dashboard')}</div>
          <div>
            <ConfigurationList
              currentConfigurationId={defaultConfigId ?? undefined}
              configurations={configurationsQuery.data ?? []}
              onConfigurationChange={(configId) => {
                changeConfig(configId);
              }}
              editConfig={(configId: number) => {
                setEdit(true);
                setEditableConfigId(configId);
              }}
              createConfig={() => {
                setEdit(true);
                setEditableConfigId(undefined);
              }}
              isSharedEditAllowed={
                claims.permissions.editTenantDashboardUIConfiguration
              }
              isPersonalEditAllowed={true}
              testId="dashboard-configurations"
              configName="dashboard"
            />
          </div>
        </div>
      )}
      <div className={styles.page}>
        <Loading
          loading={visitsQuery.isLoading}
          title={i18next.t('dashboard.loading')}
        >
          <UIConfigurator
            title={i18next.t('ui_configurator.dashboard_title')}
            blocks={blocks}
            isEditMode={isEdit}
            configType={UIConfigurationType.Dashboard}
            closeEditMode={setEdit}
            configId={
              isEdit ? editableConfigId : (defaultConfigId ?? undefined)
            }
          />
        </Loading>
      </div>
    </>
  );
});
export default Dashboard;
